import type { MessageDto, ServerEventName, ServerEventPayload } from '@lagerfeuer/shared';
import { produce } from 'immer';
import { create } from 'zustand';
import { applyEvent, initialAppData, mergePage, type PageDirection } from '../realtime/reducer';
import type { AppData, PendingMessage } from './types';

interface AppActions {
  /** Gateway-Ereignis anwenden (reiner Reducer). */
  dispatch<E extends ServerEventName>(event: E, payload: ServerEventPayload<E>): void;
  /** Lokale Änderung per immer-Rezept (z. B. nach REST-Antworten). */
  update(recipe: (d: AppData) => void): void;
  addPage(channelId: string, page: MessageDto[], direction: PageDirection, limit: number): void;
  addPending(p: PendingMessage): void;
  setPending(channelId: string, nonce: string, patch: Partial<PendingMessage> | null): void;
  reset(): void;
}

export type AppStore = AppData & AppActions;

export const useAppStore = create<AppStore>((set, get) => ({
  ...initialAppData(),
  dispatch: (event, payload) => set(applyEvent(get(), event, payload)),
  update: (recipe) => set(produce(get(), recipe)),
  addPage: (channelId, page, direction, limit) =>
    set(
      produce(get(), (d) => {
        d.messages[channelId] = mergePage(d.messages[channelId], page, direction, limit);
        for (const m of page) if (m.author && !m.webhookId) d.users[m.author.id] ??= m.author;
      }),
    ),
  addPending: (p) =>
    set(
      produce(get(), (d) => {
        (d.pending[p.channelId] ??= []).push(p);
      }),
    ),
  setPending: (channelId, nonce, patch) =>
    set(
      produce(get(), (d) => {
        const list = d.pending[channelId] ?? [];
        if (patch === null) d.pending[channelId] = list.filter((x) => x.nonce !== nonce);
        else {
          const item = list.find((x) => x.nonce === nonce);
          if (item) Object.assign(item, patch);
        }
      }),
    ),
  reset: () => set({ ...initialAppData() }),
}));

/** Direkter Zugriff außerhalb von React (Gateway, Tests). */
export const appState = (): AppStore => useAppStore.getState();
